import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BreadcrumbNav } from '../components/BreadcrumbNav';
import { TenantCard } from '../components/TenantCard';
import { getAllTenants } from '../services/portalService';
import { TenantListingCard } from '../../../core/types/portal';
import { Filter, Grid, List, MapPin } from 'lucide-react';
import { BRAZILIAN_MUNICIPALITIES } from '../data/brazilianMunicipalities';

// Configuração Rápida das Regiões Específicas Solicitadas
const REGION_CONFIG: Record<string, { name: string; state: string; emoji: string }> = {
  'vale-do-araguaia': { name: 'Vale do Araguaia', state: 'GO', emoji: '🐟' },
  'vale-da-serra-da-mesa': { name: 'Vale da Serra da Mesa', state: 'GO', emoji: '🏔️' },
  'lagos-do-paranaiba': { name: 'Lagos do Paranaíba', state: 'GO', emoji: '🚤' },
};

export const RegionDirectoryPage: React.FC = () => {
  const { region } = useParams<{ region: string }>();
  const regionConfig = region ? REGION_CONFIG[region] : null;

  const [allTenants, setAllTenants] = useState<TenantListingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // Filtros
  const [selectedCity, setSelectedCity] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [region]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Idealmente filtrariamos no backend
      const tenantsData = await getAllTenants();
      setAllTenants(tenantsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!regionConfig) {
     return (
       <div className="min-h-screen bg-brand-midnight flex flex-col items-center justify-center p-6 bg-glow-mesh-dark">
         <div className="text-center max-w-md">
           <div className="text-8xl mb-8 grayscale opacity-20">🏔️</div>
           <h1 className="text-4xl font-black text-white mb-4 font-display">Região não encontrada</h1>
           <p className="text-white/40 mb-8 font-medium italic">Esta região turística ainda não foi mapeada pelo Manél.</p>
           <button 
             onClick={() => window.history.back()}
             className="text-brand-gold hover:underline font-black uppercase tracking-widest text-xs"
           >
             ← Voltar para Explorer
           </button>
         </div>
       </div>
     );
  }

  // Encontrar cidades desta região
  const regionCities = BRAZILIAN_MUNICIPALITIES[regionConfig.state]?.filter(mun => {
      const munRegion = mun.touristicRegion?.toLowerCase() || '';
      const targetRegion = regionConfig.name.toLowerCase();
      return munRegion.includes(targetRegion);
  }) || [];

  const displayedTenants = allTenants.filter(tenant => {
    const cityInRegion = regionCities.some(c => c.name === tenant.location.city && c.state === tenant.location.state);
    if (!cityInRegion) return false;

    if (selectedCity && tenant.location.city !== selectedCity) return false;
    
    return true;
  });

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col">
      {/* Header Content */}
      <div className="bg-white/5 border-b border-white/10">
        <div className="container mx-auto px-6 py-8">
          <BreadcrumbNav 
            items={[
               { label: 'Regiões Turísticas' },
               { label: regionConfig.name }
            ]}
          />
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-black text-white mb-2 font-display">
                {regionConfig.emoji} {regionConfig.name}
              </h1>
              <p className="text-white/40 font-bold uppercase tracking-widest text-xs">
                {loading ? '...' : `${displayedTenants.length} experiencias encontradas`} em {regionCities.length} cidades turísticas
              </p>
            </div>
            
            <div className="flex gap-2">
              <button 
                onClick={() => setViewMode('grid')} 
                className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-brand-gold text-brand-midnight shadow-glow-gold' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                aria-label="Visualização em grade"
                title="Visualização em grade"
              >
                <Grid size={20} />
              </button>
              <button 
                onClick={() => setViewMode('list')} 
                className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-brand-gold text-brand-midnight shadow-glow-gold' : 'bg-white/5 text-white/40 hover:bg-white/10'}`}
                aria-label="Visualização em lista"
                title="Visualização em lista"
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Filtros e Listagem */}
      <div className="container mx-auto px-6 py-8 flex-grow">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar de Filtros */}
          <div className="lg:col-span-1">
            <div className="bg-brand-charcoal/30 rounded-2xl p-6 border border-white/10 sticky top-6">
              <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30 mb-6 flex items-center gap-2">
                <Filter size={14} className="text-brand-gold" />
                Filtros
              </h3>

              {/* Filtro por Cidade */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <MapPin size={14} /> 
                  Cidade na Região
                </h4>
                <select 
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-3 py-2.5 text-white text-sm focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all" 
                  value={selectedCity} 
                  onChange={(e) => setSelectedCity(e.target.value)}
                  title="Selecionar Cidade"
                >
                  <option value="" className="bg-brand-midnight">Todas as cidades</option>
                  {regionCities.map(mun => (
                    <option key={mun.code} value={mun.name} className="bg-brand-midnight">{mun.name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Grid de Empresas */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold"></div>
                <p className="mt-4 text-white/60 font-medium">Explorando a região...</p>
              </div>
            ) : displayedTenants.length === 0 ? (
              <div className="bg-white/5 rounded-3xl p-12 text-center border border-white/10 shadow-xl">
                <div className="text-6xl mb-4 grayscale opacity-50">🎣</div>
                <h3 className="text-xl font-black text-white mb-2 font-display">Nenhum parceiro encontrado nesta região ainda</h3>
                <p className="text-white/40 mb-6 font-medium">Estamos expandindo para o {regionConfig.name}.</p>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 gap-6' : 'space-y-4'}>
                {displayedTenants.map(tenant => (
                  <TenantCard key={tenant.id} tenant={tenant} variant={viewMode === 'list' ? 'compact' : 'default'} />
                ))}
              </div>
            )}
            
             {/* Lista de Cidades da Região (SEO Footer) */}
             <div className="mt-12 border-t border-white/10 pt-8">
                <h3 className="font-bold text-xs uppercase tracking-widest text-white/30 mb-4">Cidades do {regionConfig.name}</h3>
                <div className="flex flex-wrap gap-2">
                   {regionCities.map(city => (
                      <span key={city.code} className="bg-white/5 text-white/40 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-white/5">
                        {city.name}
                      </span>
                   ))}
                </div>
             </div>

          </div>
        </div>
      </div>
    </div>
  );
};
